/**
 * 区域网格 ⇄ 边界位串 ⇄ Base64 字符。
 * 依据 docs/sbn-v1-format.md 第 4 节实现。
 *
 * 位串用 Uint8Array（每元素 0 或 1）表示，避免字符串拼接与 parseInt 往返。
 * 两段位序不同：竖边行优先，横边列优先 —— 这是本文件唯一的易错点。
 */

import { charValue, valueChar, borderSizes } from './alphabet.js';

/**
 * 竖边段内索引：格 (r,c) 与 (r,c+1) 之间。
 * @param {number} dim @param {number} r @param {number} c
 */
const vIndex = (dim, r, c) => r * (dim - 1) + c;

/**
 * 横边段内索引：格 (r,c) 与 (r+1,c) 之间。注意是列优先。
 * @param {number} dim @param {number} r @param {number} c
 */
const hIndex = (dim, r, c) => c * (dim - 1) + r;

export { vIndex, hIndex };

/**
 * 区域网格 → 边界位串。
 * @param {number[][]} regionGrid dim×dim，值为区域 id
 * @returns {{ dim: number, vBits: Uint8Array, hBits: Uint8Array }}
 */
export function gridToBits(regionGrid) {
    const dim = regionGrid.length;
    const half = dim * (dim - 1);
    const vBits = new Uint8Array(half);
    const hBits = new Uint8Array(half);

    // 竖边：行优先
    for (let r = 0; r < dim; r++) {
        for (let c = 0; c < dim - 1; c++) {
            vBits[vIndex(dim, r, c)] =
                regionGrid[r][c] !== regionGrid[r][c + 1] ? 1 : 0;
        }
    }
    // 横边：列优先
    for (let c = 0; c < dim; c++) {
        for (let r = 0; r < dim - 1; r++) {
            hBits[hIndex(dim, r, c)] =
                regionGrid[r][c] !== regionGrid[r + 1][c] ? 1 : 0;
        }
    }
    return { dim, vBits, hBits };
}

/**
 * 边界位串 → Base64 字符串。左侧补 0 到 6 的倍数，每 6 位大端一组。
 * @param {Uint8Array} vBits @param {Uint8Array} hBits
 * @returns {string}
 */
export function bitsToChars(vBits, hBits) {
    const total = vBits.length + hBits.length;
    const pad = (6 - (total % 6)) % 6;
    const padded = new Uint8Array(pad + total); // 前 pad 位天然为 0
    padded.set(vBits, pad);
    padded.set(hBits, pad + vBits.length);

    let out = '';
    for (let i = 0; i < padded.length; i += 6) {
        let v = 0;
        for (let k = 0; k < 6; k++) v = (v << 1) | padded[i + k];
        out += valueChar(v);
    }
    return out;
}

/**
 * Base64 字符串 → 边界位串。截取末尾 borderBits 位以丢弃前导填充。
 * @param {string} data
 * @param {number} dim
 * @returns {{ vBits: Uint8Array, hBits: Uint8Array }}
 */
export function charsToBits(data, dim) {
    const { bits: borderBits } = borderSizes(dim);

    const all = new Uint8Array(data.length * 6);
    for (let i = 0; i < data.length; i++) {
        const v = charValue(data[i]);
        if (v < 0) throw new SyntaxError(`边界数据含非法字符: '${data[i]}'`);
        for (let k = 0; k < 6; k++) all[i * 6 + k] = (v >> (5 - k)) & 1;
    }
    if (all.length < borderBits) {
        throw new SyntaxError(
            `边界数据过短: 需 ${borderBits} 位，实得 ${all.length} 位`);
    }

    const start = all.length - borderBits; // 丢弃前导填充
    const half = dim * (dim - 1);
    return {
        vBits: all.subarray(start, start + half),
        hBits: all.subarray(start + half, start + borderBits),
    };
}

/**
 * 边界位串 → 区域网格。以边界为墙做洪水填充，id 从 1 起按行优先扫描顺序递增。
 * @param {number} dim
 * @param {Uint8Array} vBits
 * @param {Uint8Array} hBits
 * @returns {number[][]}
 */
export function bitsToGrid(dim, vBits, hBits) {
    const grid = Array.from({ length: dim }, () => new Array(dim).fill(0));
    let nextId = 1;
    const queue = new Int32Array(dim * dim); // 复用队列，存 r*dim+c

    for (let rs = 0; rs < dim; rs++) {
        for (let cs = 0; cs < dim; cs++) {
            if (grid[rs][cs] !== 0) continue;

            const id = nextId++;
            grid[rs][cs] = id;
            queue[0] = rs * dim + cs;
            let head = 0, tail = 1;

            while (head < tail) {
                const cur = queue[head++];
                const r = (cur / dim) | 0;
                const c = cur % dim;

                // 右
                if (c < dim - 1 && grid[r][c + 1] === 0 &&
                    vBits[vIndex(dim, r, c)] === 0) {
                    grid[r][c + 1] = id;
                    queue[tail++] = r * dim + (c + 1);
                }
                // 左
                if (c > 0 && grid[r][c - 1] === 0 &&
                    vBits[vIndex(dim, r, c - 1)] === 0) {
                    grid[r][c - 1] = id;
                    queue[tail++] = r * dim + (c - 1);
                }
                // 下
                if (r < dim - 1 && grid[r + 1][c] === 0 &&
                    hBits[hIndex(dim, r, c)] === 0) {
                    grid[r + 1][c] = id;
                    queue[tail++] = (r + 1) * dim + c;
                }
                // 上
                if (r > 0 && grid[r - 1][c] === 0 &&
                    hBits[hIndex(dim, r - 1, c)] === 0) {
                    grid[r - 1][c] = id;
                    queue[tail++] = (r - 1) * dim + c;
                }
            }
        }
    }
    return grid;
}

/** 便捷组合：Base64 字符串 → 区域网格。 */
export function decodeRegions(data, dim) {
    const { vBits, hBits } = charsToBits(data, dim);
    return bitsToGrid(dim, vBits, hBits);
}

/** 便捷组合：区域网格 → Base64 字符串。 */
export function encodeRegions(regionGrid) {
    const { vBits, hBits } = gridToBits(regionGrid);
    return bitsToChars(vBits, hBits);
}
